import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  setOrder,
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/menteeLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import getTagsQueryParams from 'utils/tagQueryParam';
import MentoringFilter from './MentoringFilter';
import MentoringList from './MentoringList';

const useCustomSWR = (
  id: number | undefined,
  take: number,
  page: number,
  tags: Tags<TagColumnNames>,
  search: string,
  tagNames: TagColumnNames[],
  order: OrderValue,
) => {
  const { data, error } = useSWR(
    `/mentorship/mentee/${id}/associate/?take=${take}&page=${page}\
		${search ? `&search=${search}` : ''}\
		${getTagsQueryParams<TagColumnNames>(tags, tagNames)}&order=${order}
		`,
  );

  if (!id) {
    return { data: undefined };
  }
  return { data, error };
};
type RetrievedData = {
  mentors: User[] | undefined;
  pages: number | undefined;
  count: number | undefined;
};

export const tagColumnNames = [
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'expertiseFields',
  'formations',
  'diploma',
  'consideration',
  'hobby',
  'wantedMentorships',
] as const;
export type TagColumnNames = typeof tagColumnNames[number];

export const sortableColumns = [
  'matching',
  'firstName',
  'lastName',
  'organism',
  'createdAt',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};

type ContextType = {
  mentee: User | undefined;
  mentors: User[] | undefined;
  pages: number | undefined;
  loading: boolean;
} & Props;
export const MenteeAssociateContext = React.createContext({} as ContextType);
const { Provider } = MenteeAssociateContext;

const Associate: FC<Props> = ({
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { id } = useParams();

  const mentee: User | undefined = useSWR(`/user/${id}`).data;

  const { data, error }: { data?: RetrievedData; error?: unknown } =
    useCustomSWR(
      parseInt(id || '') || undefined,
      take,
      page,
      tags,
      search,
      [...tagColumnNames],
      order,
    );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentors: undefined,
    count: undefined,
    pages: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      mentee,
      mentors: retrievedData.mentors,
      pages: retrievedData.pages,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      loading: !data,
      take,
      setTake,
      order,
      setOrder,
    }),
    [
      mentee,
      retrievedData,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      data,
      take,
      setTake,
      order,
      setOrder,
    ],
  );

  return error ? (
    <p>L'utilisateur n'a pas rempli son formulaire correctement.</p>
  ) : retrievedData.mentors ? (
    <Provider value={value}>
      <MentoringFilter />
      <MentoringList />
    </Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader />
    </LoaderContainer>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.menteeLists.associate.page,
    search: state.admin.menteeLists.associate.search,
    tags: state.admin.menteeLists.associate.tags,
    take: state.admin.menteeLists.associate.take,
    order: state.admin.menteeLists.associate.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('associate', page)),
    setSearch: (search: string) => dispatch(setSearch('associate', search)),
    setTags: (tags: Tags<TagColumnNames>) =>
      dispatch(setTags('associate', tags)),
    setTake: (take: number) => dispatch(setTake('associate', take)),
    setOrder: (order: OrderValue) => dispatch(setOrder('associate', order)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Associate);
